import { Link } from 'react-router-dom';

const WA_NUMBER = '919873385555';

const tiers = [
  {
    key: 'self',
    badge: 'BASIC',
    name: 'Self-Managed',
    subtitle: 'Only Server Package',
    tagline: 'You\'re in full control',
    description: 'Customer manages everything end to end. Leapswitch handles hardware health and network infrastructure only.',
    color: '#d1d5db',
    borderColor: 'rgba(107,114,128,0.4)',
    glowColor: 'rgba(107,114,128,0.15)',
    badgeBg: 'rgba(107,114,128,0.15)',
    badgeColor: '#d1d5db',
    accentBg: 'rgba(107,114,128,0.06)',
  },
  {
    key: 'semi',
    badge: 'POPULAR',
    name: 'Semi-Managed',
    subtitle: 'We Share the Work',
    tagline: 'Shared responsibility model',
    description: 'Leapswitch monitors infrastructure and assists with reported issues. You retain control, we provide expert backup.',
    color: '#00d4ff',
    borderColor: 'rgba(0,212,255,0.4)',
    glowColor: 'rgba(0,212,255,0.18)',
    badgeBg: 'rgba(0,212,255,0.12)',
    badgeColor: '#00d4ff',
    accentBg: 'rgba(0,212,255,0.05)',
    popular: true,
  },
  {
    key: 'full',
    badge: 'ENTERPRISE',
    name: 'Fully Managed',
    subtitle: 'We Do It All',
    tagline: '24/7 hands-on management',
    description: 'Continuous monitoring, automated alerts, proactive patching, and full expert intervention whenever needed.',
    color: '#a855f7',
    borderColor: 'rgba(168,85,247,0.4)',
    glowColor: 'rgba(168,85,247,0.18)',
    badgeBg: 'rgba(168,85,247,0.12)',
    badgeColor: '#a855f7',
    accentBg: 'rgba(168,85,247,0.05)',
  },
];

const tableData = [
  {
    category: '🖥️ Monitoring',
    rows: [
      { feature: 'Hardware Monitoring', self: true, semi: true, full: true },
      { feature: 'Network Devices', self: true, semi: true, full: true },
      { feature: 'System / OS', self: false, semi: 'Partial', full: true },
      { feature: 'Port & Service Monitoring', self: false, semi: true, full: true },
      { feature: 'Website Uptime', self: false, semi: false, full: true },
      { feature: 'Storage Health', self: false, semi: true, full: true },
    ],
  },
  {
    category: '⚙️ Software Management',
    rows: [
      { feature: 'OS Updates & Patching', self: false, semi: 'On request', full: true },
      { feature: 'Software Installation', self: false, semi: 'On request', full: true },
      { feature: 'Application Management', self: false, semi: false, full: true },
    ],
  },
  {
    category: '🔒 Security',
    rows: [
      { feature: 'Firewall Setup & Rules', self: false, semi: 'Basic', full: true },
      { feature: 'Intrusion Detection (IDS)', self: false, semi: false, full: true },
      { feature: 'Vulnerability Scanning', self: false, semi: false, full: true },
      { feature: 'SSL / TLS Management', self: false, semi: 'On request', full: true },
    ],
  },
  {
    category: '💾 Backup & Recovery',
    rows: [
      { feature: 'Backup Configuration', self: false, semi: 'On request', full: true },
      { feature: 'Disaster Recovery Planning', self: false, semi: false, full: true },
      { feature: 'Data Restoration', self: false, semi: 'Assisted', full: true },
    ],
  },
  {
    category: '🎧 Support',
    rows: [
      { feature: 'Response Time', self: '24–48 hr', semi: '4–8 hr', full: '15 min' },
      { feature: 'Ticket Priority', self: 'Standard', semi: 'High', full: 'Critical' },
      { feature: 'Dedicated Engineer', self: false, semi: false, full: true },
    ],
  },
];

function CellValue({ value, color }) {
  if (value === true) {
    return (
      <span style={{
        color: '#22c55e',
        fontSize: '18px',
        fontWeight: '700',
      }}>✓</span>
    );
  }
  if (value === false) {
    return (
      <span style={{
        color: '#374151',
        fontSize: '18px',
      }}>✕</span>
    );
  }
  return (
    <span style={{
      color: color || '#f59e0b',
      fontSize: '12px',
      fontWeight: '600',
      background: 'rgba(245,158,11,0.1)',
      padding: '2px 8px',
      borderRadius: '5px',
      whiteSpace: 'nowrap',
    }}>
      {value}
    </span>
  );
}

function TierCard({ tier }) {
  const waMsg = encodeURIComponent(`Hi, I'm interested in the ${tier.name} (${tier.subtitle}) plan. Can you share pricing details?`);
  const waLink = `https://wa.me/${WA_NUMBER}?text=${waMsg}`;

  return (
    <div style={{
      flex: '1 1 280px',
      minWidth: '260px',
      maxWidth: '360px',
      background: 'linear-gradient(160deg, #0a0b14 0%, #0d0e1a 100%)',
      border: `1px solid ${tier.borderColor}`,
      borderRadius: '20px',
      padding: '32px 26px',
      position: 'relative',
      boxShadow: `0 8px 48px ${tier.glowColor}`,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {tier.popular && (
        <div style={{
          position: 'absolute',
          top: '-13px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: `linear-gradient(90deg, ${tier.color}, ${tier.color}bb)`,
          color: '#fff',
          fontSize: '11px',
          fontWeight: '800',
          padding: '4px 16px',
          borderRadius: '20px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          ★ Most Popular
        </div>
      )}

      {/* Badge */}
      <div style={{
        display: 'inline-block',
        background: tier.badgeBg,
        border: `1px solid ${tier.borderColor}`,
        color: tier.badgeColor,
        fontSize: '11px',
        fontWeight: '800',
        padding: '4px 10px',
        borderRadius: '6px',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: '18px',
        alignSelf: 'flex-start',
      }}>
        {tier.badge}
      </div>

      <h2 style={{
        fontSize: '24px',
        fontWeight: '800',
        color: '#fff',
        margin: '0 0 4px',
        lineHeight: 1.1,
      }}>
        {tier.name}
      </h2>
      <div style={{
        fontSize: '14px',
        color: tier.color,
        fontWeight: '600',
        marginBottom: '14px',
      }}>
        {tier.subtitle}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#d1d5db',
        fontStyle: 'italic',
        marginBottom: '16px',
      }}>
        "{tier.tagline}"
      </div>
      <p style={{
        fontSize: '14px',
        color: '#d1d5db',
        lineHeight: '1.65',
        margin: '0 0 24px',
        flexGrow: 1,
      }}>
        {tier.description}
      </p>

      {/* Price */}
      <div style={{
        background: tier.accentBg,
        border: `1px solid ${tier.borderColor}`,
        borderRadius: '10px',
        padding: '14px 16px',
        marginBottom: '20px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '13px', color: '#d1d5db', marginBottom: '4px' }}>Pricing</div>
        <Link
          to="/contact"
          style={{
            fontSize: '18px',
            fontWeight: '700',
            color: tier.color,
            textDecoration: 'none',
          }}
        >
          Contact Sales →
        </Link>
      </div>

      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          background: 'linear-gradient(135deg, #25D366, #128C7E)',
          color: '#fff',
          fontWeight: '700',
          fontSize: '14px',
          padding: '12px 0',
          borderRadius: '10px',
          textDecoration: 'none',
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.852L0 24l6.317-1.508A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.021-1.374l-.36-.214-3.748.894.928-3.657-.234-.376A9.818 9.818 0 1112 21.818z"/>
        </svg>
        Get Quote on WhatsApp
      </a>
    </div>
  );
}

export default function ManagedServices() {
  const waLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi, I'd like to learn about your Managed Server Services. Can you help me choose the right plan?")}`;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#030305',
      paddingTop: 100,
      color: '#e2e8f0',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      paddingBottom: '80px',
    }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(180deg, #08090f 0%, #030305 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '72px 24px 56px',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-block',
          background: 'rgba(168,85,247,0.1)',
          border: '1px solid rgba(168,85,247,0.25)',
          color: '#a855f7',
          fontSize: '12px',
          fontWeight: '700',
          padding: '5px 14px',
          borderRadius: '20px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: '20px',
        }}>
          Flexible Management Tiers
        </div>
        <h1 style={{
          fontSize: 'clamp(30px, 5vw, 54px)',
          fontWeight: '800',
          margin: '0 0 16px',
          lineHeight: 1.1,
          background: 'linear-gradient(135deg, #ffffff 0%, #a0aec0 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Managed Services
        </h1>
        <p style={{
          fontSize: '18px',
          color: '#d1d5db',
          maxWidth: '580px',
          margin: '0 auto',
          lineHeight: 1.6,
        }}>
          Choose how much we manage — from bare server access to a fully-staffed dedicated ops team running your infrastructure 24/7.
        </p>
      </div>

      <div style={{ maxWidth: '1140px', margin: '0 auto', padding: '0 24px' }}>
        {/* Tier Cards */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px',
          justifyContent: 'center',
          marginTop: '56px',
          marginBottom: '64px',
        }}>
          {tiers.map(tier => (
            <TierCard key={tier.key} tier={tier} />
          ))}
        </div>

        {/* Comparison Table */}
        <div style={{ marginBottom: '64px' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '28px',
            fontWeight: '800',
            color: '#fff',
            marginBottom: '8px',
          }}>
            Feature Comparison
          </h2>
          <p style={{
            textAlign: 'center',
            color: '#d1d5db',
            fontSize: '15px',
            marginBottom: '36px',
          }}>
            See exactly what's covered in each management tier
          </p>

          <div style={{
            overflowX: 'auto',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
            background: '#0a0b14',
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '600px',
            }}>
              <thead>
                <tr style={{ background: '#0d0e1a', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#d1d5db',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    width: '36%',
                  }}>
                    Feature / Service
                  </th>
                  {tiers.map(tier => (
                    <th key={tier.key} style={{
                      padding: '16px 12px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: '800',
                      color: tier.color,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      width: '21%',
                    }}>
                      {tier.name.split(' ')[0]}
                      <div style={{ fontSize: '11px', fontWeight: '500', color: '#d1d5db', marginTop: '2px', textTransform: 'none', letterSpacing: 0 }}>
                        {tier.subtitle}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((section, si) => (
                  <>
                    <tr key={`section-${si}`} style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <td
                        colSpan={4}
                        style={{
                          padding: '10px 20px',
                          fontSize: '13px',
                          fontWeight: '700',
                          color: '#d1d5db',
                          borderTop: si > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {section.category}
                      </td>
                    </tr>
                    {section.rows.map((row, ri) => (
                      <tr
                        key={`${si}-${ri}`}
                        style={{
                          borderTop: '1px solid rgba(255,255,255,0.04)',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{
                          padding: '13px 20px',
                          fontSize: '14px',
                          color: '#cbd5e1',
                        }}>
                          {row.feature}
                        </td>
                        <td style={{ padding: '13px 12px', textAlign: 'center' }}>
                          <CellValue value={row.self} color={tiers[0].color} />
                        </td>
                        <td style={{ padding: '13px 12px', textAlign: 'center', background: 'rgba(0,212,255,0.02)' }}>
                          <CellValue value={row.semi} color={tiers[1].color} />
                        </td>
                        <td style={{ padding: '13px 12px', textAlign: 'center' }}>
                          <CellValue value={row.full} color={tiers[2].color} />
                        </td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{
          background: 'linear-gradient(135deg, #0c0d1a 0%, #10112a 100%)',
          border: '1px solid rgba(168,85,247,0.2)',
          borderRadius: '20px',
          padding: '48px 32px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Glow accent */}
          <div style={{
            position: 'absolute',
            top: '-60px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '400px',
            height: '200px',
            background: 'radial-gradient(ellipse, rgba(168,85,247,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{
            display: 'inline-block',
            background: 'rgba(168,85,247,0.12)',
            border: '1px solid rgba(168,85,247,0.3)',
            color: '#a855f7',
            fontSize: '11px',
            fontWeight: '700',
            padding: '4px 12px',
            borderRadius: '20px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '18px',
          }}>
            Not sure which tier? Let's talk.
          </div>

          <h3 style={{
            fontSize: '26px',
            fontWeight: '800',
            color: '#fff',
            margin: '0 0 10px',
          }}>
            Let Our Team Recommend the Right Plan
          </h3>
          <p style={{
            color: '#d1d5db',
            fontSize: '15px',
            maxWidth: '520px',
            margin: '0 auto 28px',
            lineHeight: 1.65,
          }}>
            Every infrastructure is unique. Speak to a Leapswitch expert who can assess your workloads and suggest the optimal management level.
          </p>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                color: '#fff',
                fontWeight: '700',
                fontSize: '15px',
                padding: '14px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.852L0 24l6.317-1.508A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.021-1.374l-.36-.214-3.748.894.928-3.657-.234-.376A9.818 9.818 0 1112 21.818z"/>
              </svg>
              Talk to an Expert
            </a>
            <Link
              to="/contact"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(168,85,247,0.08)',
                border: '1px solid rgba(168,85,247,0.3)',
                color: '#a855f7',
                fontWeight: '700',
                fontSize: '15px',
                padding: '14px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.08)'; }}
            >
              Contact Sales →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
